import React, { useState, useEffect, KeyboardEvent, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';

import {
    Button,
    GradientDot,
    IconInfoCircle,
    IconTrqkMark,
    LoadingEllipses,
    ValidatedInput,
} from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import {
    constants,
    encodeURLParams,
    getWebpackEnvVar,
    locale,
} from '@trqk/utils';
import { validateInputs } from '@trqk/hooks';

import { AuthLayout } from '../../templates';
import { PATHS, URL_PARAMS } from '../../../constants';
import { useToast } from '../../../hooks';
import { InvitationRequiredToast } from './InvitationRequiredToast';
import { inputIds } from './constants';
import { initialStateData, INITIAL_STATE } from '../../../app/initialState';

const registerInvitedUserUrl = getWebpackEnvVar(
    'web',
    'REGISTER_INVITED_USER_URL',
);
const { REGISTER_INVITED_USER, SIGN_IN_WITH_COGNITO, SIGN_OUT_WITH_COGNITO } =
    queryStrings;
const { API_ERROR_MESSAGES } = constants;

const centerColumnWidth = 350;

// The CRM requires both these fields to be present when creating a contact, so we use dummy values
// in the contact creation step. We then have to check for dummy values here and avoid using them in
// the form.
const dummyFirstName = 'First';
const dummyLastName = 'Last';
const dummyCompany = 'Company';

/**
 * A form that allows a user to create an account. If URL parameters for `name`, `company`, and
 * `email` are present, they will be used to pre-populate the form.
 */
export const SignUp = (): JSX.Element => {
    const clientId = getWebpackEnvVar('web', 'AWS_APP_CLIENT_ID');
    const userPoolId = getWebpackEnvVar('web', 'AWS_USER_POOL_ID');
    const toast = useToast();
    const urlQuery = new URLSearchParams(useLocation().search);
    const firstNameParam = urlQuery.get(URL_PARAMS.FIRST_NAME);
    const initialFirstName =
        firstNameParam === dummyFirstName ? '' : firstNameParam ?? '';
    const lastNameParam = urlQuery.get(URL_PARAMS.LAST_NAME);
    const initialLastName =
        lastNameParam === dummyLastName ? '' : lastNameParam ?? '';
    const companyParam = urlQuery.get(URL_PARAMS.COMPANY);
    const initialCompany =
        companyParam === dummyCompany ? '' : companyParam ?? '';
    const initialEmail = urlQuery.get(URL_PARAMS.EMAIL) ?? '';
    const statementId = urlQuery.get(URL_PARAMS.STATEMENT_ID) ?? undefined;
    const [firstName, setFirstName] = useState(initialFirstName);
    const [lastName, setLastName] = useState(initialLastName);
    const [company, setCompany] = useState(initialCompany);
    const [email, setEmail] = useState(initialEmail);
    const [password, setPassword] = useState('');
    const [didSubmit, setDidSubmit] = useState(false);
    const [validationOutputs, setValidationOutputs] = useState<
        ValidationOutput[]
    >([]);
    const [signUpErrorMessage, setSignUpErrorMessage] = useState('');
    const history = useHistory();

    const client = useApolloClient();

    const [signOutWithCognito] = useMutation(SIGN_OUT_WITH_COGNITO);

    useEffect((): void => {
        // Ensure that the Apollo Client cache is reset and the user is signed out if they visit
        // this page
        client.resetStore();
        signOutWithCognito({
            variables: {
                clientId,
                userPoolId,
            },
            context: {
                initialStateData,
                initialStateQuery: INITIAL_STATE,
            },
        });
    }, [client, signOutWithCognito]);

    const [signInWithCognito, signInWithCognitoQuery] = useMutation(
        SIGN_IN_WITH_COGNITO,
        {
            onCompleted: data => {
                const error = data?.signInWithCognito?.error ?? null;

                if (error) {
                    setSignUpErrorMessage(
                        locale.enUS.SIGN_UP.ERRORS.REGISTER_INVITED_USER,
                    );
                } else {
                    // Redirect to Insights page if user creation and authentication was
                    // successful
                    history.push(PATHS.INSIGHTS);
                }
            },
        },
    );

    const [registerInvitedUser, registerInvitedUserQuery] = useMutation(
        REGISTER_INVITED_USER,
        {
            onCompleted: (data): void => {
                const status = data?.registerInvitedUser?.status ?? 'failure';
                const message = data?.registerInvitedUser?.message ?? null;

                if (status === 'success') {
                    // If user was successfully created, authenticate the new user
                    signInWithCognito({
                        variables: {
                            username: email,
                            password,
                            clientId,
                            userPoolId,
                            updateUiState: true,
                        },
                    });
                } else if (
                    message === API_ERROR_MESSAGES.ACCOUNT_ALREADY_EXISTS
                ) {
                    // Redirect to the SignIn page and inform them that the username already exists
                    history.push(
                        `${PATHS.SIGN_IN}?${encodeURLParams({
                            [URL_PARAMS.USER_ALREADY_EXISTS]: email,
                        })}`,
                    );
                } else if (status === 'WAITLISTED') {
                    // Warn the user that an invitation is required to create an account, and
                    // expose a mailto: link they can use to contact us if they think there has been
                    // a mistake.
                    toast.add({
                        RenderContent: InvitationRequiredToast,
                        kind: 'warning-card',
                        renderContentInput: {
                            email,
                        },
                        persist: true,
                    });
                } else if (status === 'failure') {
                    // Attempt to show the message sent back by the endpoint
                    if (message !== null) {
                        toast.add({
                            kind: 'warning-banner',
                            content: message,
                        });
                    } else {
                        // Default to generic message if necessary
                        toast.add({
                            kind: 'warning-banner',
                            content: locale.enUS.DEFAULT_API_ERROR,
                        });
                    }
                }
            },
            onError: (): void => {
                setSignUpErrorMessage(
                    locale.enUS.SIGN_UP.ERRORS.REGISTER_INVITED_USER,
                );
            },
        },
    );

    const loading =
        signInWithCognitoQuery.loading || registerInvitedUserQuery.loading;

    const validate = useCallback(
        () =>
            validateInputs([
                {
                    inputId: inputIds.signUpFirstName,
                    label: 'First name',
                    required: true,
                    type: 'name',
                    value: firstName,
                },
                {
                    inputId: inputIds.signUpLastName,
                    label: 'Last name',
                    required: true,
                    type: 'name',
                    value: lastName,
                },
                {
                    inputId: inputIds.signUpCompany,
                    label: 'Company',
                    required: true,
                    type: 'company',
                    value: company,
                },
                {
                    inputId: inputIds.signUpEmail,
                    label: 'Email',
                    required: true,
                    type: 'email',
                    value: email,
                },
                {
                    inputId: inputIds.signUpPassword,
                    label: 'Password',
                    required: true,
                    type: 'password',
                    value: password,
                },
            ]),
        [company, email, firstName, lastName, password],
    );

    useEffect(() => {
        if (didSubmit) {
            const validationResults = validate();

            setValidationOutputs(validationResults.validationOutputs);
        }
    }, [didSubmit, validate]);

    const handleSubmit = (): void => {
        setDidSubmit(true);

        const validationResults = validate();

        if (!validationResults.hasValidationErrors) {
            registerInvitedUser({
                variables: {
                    company,
                    email,
                    firstName,
                    lastName,
                    password,
                    registerInvitedUserUrl,
                    statementId,
                },
            });
        } else {
            setValidationOutputs(validationResults.validationOutputs);
        }
    };

    return (
        <AuthLayout>
            <div className="flex flex-col h-full">
                <div className="flex-grow flex flex-col justify-center items-center">
                    <div style={{ width: centerColumnWidth }}>
                        <div className="flex justify-center">
                            <GradientDot gradientColor="white" width={120}>
                                <IconTrqkMark width={50} />
                            </GradientDot>
                        </div>
                        <form>
                            <div>
                                <h3 className="text-white font-medium text-center my-4">
                                    {locale.enUS.SIGN_UP.TITLE}
                                </h3>
                                <div className="my-4" style={{ minHeight: 30 }}>
                                    {signUpErrorMessage && (
                                        <div className="text-center">
                                            <span
                                                className="flex-none"
                                                style={{ height: 20 }}
                                            >
                                                <IconInfoCircle
                                                    addClasses="inline"
                                                    fillColor="white"
                                                />
                                            </span>
                                            <span className="ml-1 text-sm text-white">
                                                {signUpErrorMessage}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <ValidatedInput
                                    addClasses="my-2"
                                    inputId={inputIds.signUpFirstName}
                                    icon="name"
                                    type="name"
                                    placeholder="First name"
                                    value={firstName}
                                    handleChange={setFirstName}
                                    padding="large"
                                    validationOutputs={validationOutputs}
                                />
                                <ValidatedInput
                                    addClasses="my-2"
                                    inputId={inputIds.signUpLastName}
                                    icon="name"
                                    type="name"
                                    placeholder="Last name"
                                    value={lastName}
                                    handleChange={setLastName}
                                    padding="large"
                                    validationOutputs={validationOutputs}
                                />
                                <ValidatedInput
                                    addClasses="my-2"
                                    inputId={inputIds.signUpCompany}
                                    icon="company"
                                    type="text"
                                    placeholder="Company"
                                    value={company}
                                    handleChange={setCompany}
                                    padding="large"
                                    validationOutputs={validationOutputs}
                                />
                                <ValidatedInput
                                    addClasses="my-2"
                                    autoComplete="username"
                                    inputId={inputIds.signUpEmail}
                                    icon="email"
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    handleChange={setEmail}
                                    padding="large"
                                    validationOutputs={validationOutputs}
                                />
                                <ValidatedInput
                                    addClasses="my-2"
                                    autoComplete="new-password"
                                    inputId={inputIds.signUpPassword}
                                    icon="password"
                                    type="password"
                                    placeholder="Password"
                                    value={password}
                                    handleChange={setPassword}
                                    handleKeyPress={(
                                        e: KeyboardEvent<Element>,
                                    ): void => {
                                        if (e.key === 'Enter') {
                                            handleSubmit();
                                        }
                                    }}
                                    padding="large"
                                    validationOutputs={validationOutputs}
                                />
                            </div>
                            <div className="mt-10">
                                <Button
                                    color="primary-dark-bg"
                                    handleClick={handleSubmit}
                                >
                                    <div className="text-center tracking-wider">
                                        {!loading && (
                                            <div>
                                                {
                                                    locale.enUS.SIGN_UP.BUTTONS
                                                        .CREATE_ACCOUNT
                                                }
                                            </div>
                                        )}
                                        {loading && (
                                            <LoadingEllipses width={40} />
                                        )}
                                    </div>
                                </Button>
                            </div>
                        </form>
                        <div className="flex justify-center text-white text-sm mt-4">
                            <div>
                                {locale.enUS.SIGN_UP.ALREADY_HAVE_AN_ACCOUNT}
                                &nbsp;
                                <button
                                    className="font-medium underline"
                                    type="button"
                                    onClick={(): void => {
                                        history.push(PATHS.SIGN_IN);
                                    }}
                                >
                                    {locale.enUS.SIGN_UP.SIGN_IN_HERE}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
};
